import DOMPurify from "isomorphic-dompurify";

const purifyConfig = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "em",
    "code",
    "pre",
    "a",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "blockquote",
    "hr",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
  ] as string[],
  // `align` (not `style`): table alignment needs exactly one presentational
  // attribute, and allowing `style` here would let any HTML reaching the
  // sanitizedHtml seam carry arbitrary CSS (overlays, remote url() beacons).
  ALLOWED_ATTR: ["href", "target", "rel", "align"] as string[],
  RETURN_TRUSTED_TYPE: false as const,
};

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function formatInlineMarkdown(text: string): string {
  let formatted = escapeHtml(text);

  formatted = formatted.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  // A page reference, [title](uuid), or an old Library link,
  // [title](/knowledge/<id>), reads as its title: a page has no address of its
  // own since the Library's reading view retired, and /knowledge/<id> lands on
  // Waiting on you. The /knowledge/decisions shapes still redirect, so they stay.
  formatted = formatted.replace(
    /\[([^\]]+)\]\(([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)/g,
    "$1",
  );
  formatted = formatted.replace(/\[([^\]]+)\]\(\/knowledge\/(?!decisions\b)[^\s)]+\)/g, "$1");
  // Relative in-app links: [title](/path), e.g. a ticket on its Team's queue.
  formatted = formatted.replace(/\[([^\]]+)\]\((\/(?!\/)[^\s)]+)\)/g, '<a href="$2">$1</a>');
  formatted = formatted.replace(/`([^`]+)`/g, "<code>$1</code>");
  formatted = formatted.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  formatted = formatted.replace(/\*([^*]+)\*/g, "<em>$1</em>");

  return formatted;
}

function parseTableCells(row: string): string[] {
  return row
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
}

// GFM delimiter row: cells of dashes with optional alignment colons
// (---, :---, :---:, ---:), tolerating surrounding whitespace and missing
// leading/trailing pipes. Requires a pipe or colon so a bare `---` stays an hr.
function isTableSeparatorRow(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.includes("-") || !/[|:]/.test(trimmed)) {
    return false;
  }
  const cells = parseTableCells(trimmed);
  // Empty cells tolerate `| --- | |` typos and trailing pipes; at least one
  // real dash cell is required so a stray `|` line is not a separator.
  return (
    cells.some((cell) => /^:?-+:?$/.test(cell)) &&
    cells.every((cell) => cell === "" || /^:?-+:?$/.test(cell))
  );
}

function tableCellAlignment(cell: string): "left" | "center" | "right" | null {
  const left = cell.startsWith(":");
  const right = cell.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  if (left) return "left";
  return null;
}

/**
 * True when the text is ONE GFM pipe table block: a header row of pipes
 * followed by the separator row. The same strict shape renders as a table
 * downstream (parseTableCells/isTableSeparatorRow), so a surface can branch
 * on "this line is a table" without re-implementing detection — and prose
 * with a stray pipe never qualifies (no separator row).
 */
export function isTableBlockText(text: string): boolean {
  const rows = text.trim().split("\n");
  if (rows.length < 2) return false;
  return rows[0]!.includes("|") && isTableSeparatorRow(rows[1]!);
}

function flushParagraph(lines: string[], output: string[]) {
  if (lines.length === 0) {
    return;
  }
  output.push(`<p>${lines.map(formatInlineMarkdown).join("<br />")}</p>`);
  lines.length = 0;
}

/**
 * Strip markdown syntax down to plain text for one-line/clamped contexts
 * (card descriptions, search haystacks) where rendered block elements would
 * wreck the layout. Lossy by design: links keep their label, code keeps its
 * content, structure markers disappear.
 */
export function stripMarkdownInline(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*[-*•]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

export function renderMarkdown(markdown: string): string {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const output: string[] = [];
  const paragraphBuffer: string[] = [];

  let inCodeFence = false;
  let codeFenceBuffer: string[] = [];
  let inUnorderedList = false;
  let inOrderedList = false;
  let inTable = false;
  let tableRows: string[] = [];

  function closeLists() {
    if (inUnorderedList) {
      output.push("</ul>");
      inUnorderedList = false;
    }
    if (inOrderedList) {
      output.push("</ol>");
      inOrderedList = false;
    }
  }

  function flushTable() {
    if (!inTable || tableRows.length === 0) {
      inTable = false;
      return;
    }
    const headerCells = parseTableCells(tableRows[0]);
    const hasSeparator = tableRows.length > 1 && isTableSeparatorRow(tableRows[1]);
    const alignments = hasSeparator ? parseTableCells(tableRows[1]).map(tableCellAlignment) : [];
    const alignStyle = (index: number) => {
      const alignment = alignments[index];
      return alignment ? ` align="${alignment}"` : "";
    };

    const rows: string[] = [];
    rows.push("<table>");
    if (hasSeparator) {
      rows.push("<thead><tr>");
      headerCells.forEach((cell, index) => {
        rows.push(`<th${alignStyle(index)}>${formatInlineMarkdown(cell)}</th>`);
      });
      rows.push("</tr></thead>");
    }
    rows.push("<tbody>");
    const bodyStart = hasSeparator ? 2 : 0;
    for (let i = bodyStart; i < tableRows.length; i++) {
      const cells = parseTableCells(tableRows[i]);
      rows.push("<tr>");
      cells.forEach((cell, index) => {
        rows.push(`<td${alignStyle(index)}>${formatInlineMarkdown(cell)}</td>`);
      });
      rows.push("</tr>");
    }
    rows.push("</tbody></table>");
    output.push(rows.join(""));
    tableRows = [];
    inTable = false;
  }

  function closeCodeFence() {
    if (!inCodeFence) {
      return;
    }
    output.push(`<pre><code>${escapeHtml(codeFenceBuffer.join("\n"))}</code></pre>`);
    codeFenceBuffer = [];
    inCodeFence = false;
  }

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    if (line.trim().startsWith("```")) {
      flushParagraph(paragraphBuffer, output);
      closeLists();
      if (inCodeFence) {
        closeCodeFence();
      } else {
        inCodeFence = true;
      }
      continue;
    }

    if (inCodeFence) {
      codeFenceBuffer.push(line);
      continue;
    }

    if (line.trim().length === 0) {
      flushParagraph(paragraphBuffer, output);
      closeLists();
      flushTable();
      continue;
    }

    // Table detection runs before the other block forms so a buffered table
    // flushes IN PLACE when any non-table block follows it; otherwise the
    // table would trail the next block in the output (only EOF flushed it).
    const isPipeBoundedRow = /^\s*\|.+\|\s*$/.test(line);
    // A header row without leading/trailing pipes still starts a table when
    // the next line is a GFM delimiter row; once in a table, any line with a
    // pipe (or a bare delimiter row) continues it, per GFM.
    const startsUnpipedTable =
      !inTable &&
      line.includes("|") &&
      lineIndex + 1 < lines.length &&
      isTableSeparatorRow(lines[lineIndex + 1]);
    const continuesTable = inTable && (line.includes("|") || isTableSeparatorRow(line));
    if (isPipeBoundedRow || startsUnpipedTable || continuesTable) {
      flushParagraph(paragraphBuffer, output);
      closeLists();
      if (!inTable) {
        inTable = true;
        tableRows = [];
      }
      tableRows.push(line);
      continue;
    }
    flushTable();

    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph(paragraphBuffer, output);
      closeLists();
      const level = headingMatch[1].length;
      output.push(`<h${level}>${formatInlineMarkdown(headingMatch[2].trim())}</h${level}>`);
      continue;
    }

    // `• ` is a bullet too: Harper filings published before the markdown
    // cutover carry the glyph baked into the body text, and treating it as
    // a list item (rather than a literal glyph glued to the line) gives
    // those stories the same list semantics new filings get from `- `.
    const unorderedListMatch = line.match(/^\s*(?:[-•])\s+(.*)$/);
    if (unorderedListMatch) {
      flushParagraph(paragraphBuffer, output);
      if (inOrderedList) {
        output.push("</ol>");
        inOrderedList = false;
      }
      if (!inUnorderedList) {
        output.push("<ul>");
        inUnorderedList = true;
      }
      output.push(`<li>${formatInlineMarkdown(unorderedListMatch[1])}</li>`);
      continue;
    }

    const orderedListMatch = line.match(/^\s*\d+\.\s+(.*)$/);
    if (orderedListMatch) {
      flushParagraph(paragraphBuffer, output);
      if (inUnorderedList) {
        output.push("</ul>");
        inUnorderedList = false;
      }
      if (!inOrderedList) {
        output.push("<ol>");
        inOrderedList = true;
      }
      output.push(`<li>${formatInlineMarkdown(orderedListMatch[1])}</li>`);
      continue;
    }

    const quoteMatch = line.match(/^\s*>\s?(.*)$/);
    if (quoteMatch) {
      flushParagraph(paragraphBuffer, output);
      closeLists();
      output.push(`<blockquote>${formatInlineMarkdown(quoteMatch[1])}</blockquote>`);
      continue;
    }

    if (/^\s*---+\s*$/.test(line)) {
      flushParagraph(paragraphBuffer, output);
      closeLists();
      output.push("<hr />");
      continue;
    }

    paragraphBuffer.push(line);
  }

  flushParagraph(paragraphBuffer, output);
  closeLists();
  if (inTable) {
    flushTable();
  }
  closeCodeFence();

  return sanitizeRenderedHtml(output.join("\n"));
}

export function sanitizeRenderedHtml(html: string): string {
  return DOMPurify.sanitize(html, purifyConfig) as string;
}

type SanitizedHtmlProps = { dangerouslySetInnerHTML: { __html: string } };

/**
 * The single audited way to inject agent-authored markdown as HTML. Renders
 * and DOMPurify-sanitizes `markdown`, then returns the React
 * `dangerouslySetInnerHTML` prop object so callers can never hand-roll an
 * unsanitized injection. Spread onto a container, keeping its own class:
 *
 *   <div className="markdown-rendered" {...markdownHtml(text)} />
 */
export function markdownHtml(markdown: string): SanitizedHtmlProps {
  return { dangerouslySetInnerHTML: { __html: renderMarkdown(markdown) } };
}

/**
 * As `markdownHtml`, but for HTML that was already rendered upstream (e.g. a
 * value produced by `renderMarkdown` before being passed down as a prop).
 * Re-sanitizes defensively — DOMPurify is idempotent on already-clean markup
 * — so the injection point is provably safe regardless of the caller.
 */
export function sanitizedHtml(html: string): SanitizedHtmlProps {
  return { dangerouslySetInnerHTML: { __html: sanitizeRenderedHtml(html) } };
}
